import styled, { css, keyframes } from 'styled-components';
import { down } from 'styled-breakpoints';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export const Container = styled.aside`
  ${({ theme }) => css`
    position: fixed;
    right: 0;
    top: ${theme.layout.header};
    width: 420px;
    height: calc(100vh - ${theme.layout.header});
    overflow-y: auto;
    padding: 1.25rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    background: ${theme.colors.background[100]};
    border-left: 1px solid ${theme.colors.background[40]};

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.background[40]};
      border-radius: 4px;
    }

    ${down('xl')} {
      display: none;
    }
  `}
`;

export const Header = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    padding: 0 0.25rem;

    h3 {
      font-size: 0.9375rem;
      font-weight: 700;
      color: ${theme.colors.foreground[100]};
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  `}
`;

export const VideoCount = styled.span`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${theme.colors.brand.primary.default};
    background: ${theme.colors.brand.primary.opaque};
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
  `}
`;

export const VideoItem = styled.div<{ $playing?: boolean }>`
  ${({ theme, $playing }) => css`
    display: flex;
    gap: 0.75rem;
    padding: 0.5rem;
    border-radius: 0.625rem;
    cursor: pointer;
    transition: background ${theme.transition.default};

    :hover {
      background: ${theme.colors.background[80]};
    }

    ${$playing &&
    css`
      background: ${theme.colors.brand.primary.opaque};
      border: 1.5px solid ${theme.colors.brand.primary.default};
    `}
  `}
`;

export const Thumbnail = styled.div`
  width: 8rem;
  height: 4.5rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ThumbnailPlayOverlay = styled.div`
  ${({ theme }) => css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.25);
    opacity: 0;
    transition: opacity ${theme.transition.default};
    color: white;

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }

    ${Thumbnail}:hover & {
      opacity: 1;
    }
  `}
`;

export const VideoInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
`;

export const VideoTitle = styled.p`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

export const VideoMeta = styled.span`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    color: ${theme.colors.foreground[40]};
    display: flex;
    align-items: center;
    gap: 0.375rem;
  `}
`;

export const NowPlayingDot = styled.span`
  ${({ theme }) => css`
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: ${theme.colors.brand.primary.default};
    display: inline-block;
    margin-right: 0.375rem;
    animation: ${pulse} 1.5s ease-in-out infinite;
    vertical-align: middle;
  `}
`;

export const SectionDivider = styled.hr`
  ${({ theme }) => css`
    height: 1px;
    background: ${theme.colors.background[40]};
    margin: 0.75rem 0.25rem;
    border: none;
  `}
`;

export const SectionLabel = styled.p`
  ${({ theme }) => css`
    font-size: 0.75rem;
    font-weight: 700;
    color: ${theme.colors.foreground[40]};
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 0 0.5rem;
    margin-bottom: 0.75rem;
  `}
`;
